import {
  Box,
  Paper,
  Title,
  Text,
  TextInput,
  PasswordInput,
  Button,
  Flex,
  Select,
  Container,
  SimpleGrid,
  Anchor,
  Textarea,
  Checkbox,
  Stepper,
  Badge,
  FileButton,
} from "@mantine/core";
import {
  IconBuilding,
  IconMapPin,
  IconMail,
  IconLock,
  IconWorld,
  IconId,
  IconUpload,
  IconDownload,
  IconFileText,
} from "@tabler/icons-react";
import { errorNotification } from "../../shared/utils/errorNotification";
import { useState, useMemo, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import VerifyEmailModal from "../signup/VerifyModal";
import { countriesData } from "../../components/shared/constants/general.const";
import trpc from "../../shared/utils/trpc";
import { saveToken, removeToken } from "../../shared/utils/trpc";
import { handleTrpcError } from "../../shared/utils/trpc/handletrpcError.util";
import { successNotification } from "../../shared/utils/successNotification";
import "react-international-phone/style.css";
import { PhoneInput } from "react-international-phone";

function SignupPage() {
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);
  const [emailModalOpened, setEmailModalOpened] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sameAsRegistered, setSameAsRegistered] = useState(false);

  const [formData, setFormData] = useState({
    companyName: "",
    name: "",
    designation: "",
    phoneNumber: "",
    phoneCountryCode: "",
    country: "",
    city: "",
    registrationNumber: "",
    registrationIdType: null as string | null,
    email: "",
    password: "",
    confirmPassword: "",
    registeredAddress: "",
    currentAddress: "",
  });

  /* ---------------- Document State ---------------- */

  const [authorityLetterTemplateUrl, setAuthorityLetterTemplateUrl] = useState<string | undefined>(undefined);
  const [affidavitTemplateUrl, setAffidavitTemplateUrl] = useState<string | undefined>(undefined);
  const [mandatoryData, setMandatoryData] = useState<{ name: string; file: File | null; url: string }>({
    name: "Affidavit",
    file: null,
    url: "",
  });
  const [authorityLetterData, setAuthorityLetterData] = useState<{ name: string; file: File | null }>({
    name: "Authority Letter",
    file: null,
  });

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const r = await trpc.getTemplate.authorityLetter.query();
        setAuthorityLetterTemplateUrl(r.url);
      } catch {
        console.log("Authority letter template not available yet");
      }
      try {
        const r = await trpc.getTemplate.affidavit.query();
        setAffidavitTemplateUrl(r.url);
      } catch {
        console.log("Affidavit template not available yet");
      }
    };
    fetchTemplates();
  }, []);

  const hasAuthorityLetter = !!authorityLetterData.file;
  const hasAffidavit = !!mandatoryData.file;
  const hasRequiredDocument = hasAuthorityLetter || hasAffidavit;

  const handleUpdateMandatory = (field: string, value: any) =>
    setMandatoryData((prev) => ({ ...prev, [field]: value }));
  const handleUpdateAuthorityLetter = (field: string, value: any) =>
    setAuthorityLetterData((prev) => ({ ...prev, [field]: value }));

  /* ---------------- Validation ---------------- */

  const PERSONAL_EMAIL_DOMAINS = [
    "gmail.com", "yahoo.com", "yahoo.in", "yahoo.co.uk", "icloud.com", "me.com", "mac.com",
    "hotmail.com", "hotmail.in", "hotmail.co.uk", "outlook.com", "outlook.in",
    "live.com", "live.in", "msn.com", "aol.com", "protonmail.com", "proton.me",
    "zoho.com", "yandex.com", "yandex.ru", "mail.com", "gmx.com", "gmx.net",
    "rediffmail.com", "inbox.com", "fastmail.com", "tutanota.com",
  ];
  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isBusinessEmail = (email: string) => {
    const domain = email.split("@")[1]?.toLowerCase();
    return domain ? !PERSONAL_EMAIL_DOMAINS.includes(domain) : true;
  };

  const isPasswordValid = formData.password.length >= 8;

  const doPasswordsMatch = formData.confirmPassword === "" || formData.password === formData.confirmPassword;

  const isIndia = formData.country === "India";

  const isFormValid =
    formData.name.trim() !== "" &&
    formData.designation.trim() !== "" &&
    formData.phoneNumber.trim() !== "" &&
    formData.companyName.trim() !== "" &&
    formData.country !== "" &&
    formData.city.trim() !== "" &&
    formData.registrationNumber.trim() !== "" &&
    (isIndia ? formData.registrationIdType !== "" : true) &&
    isValidEmail(formData.email) &&
    isBusinessEmail(formData.email) &&
    isPasswordValid &&
    formData.password === formData.confirmPassword &&
    formData.registeredAddress.trim() !== "" &&
    (sameAsRegistered || formData.currentAddress.trim() !== "");

  /* ---------------- Country Options ---------------- */

  const countryOptions = useMemo(() => {
    return countriesData.countryGroups.map((group) => ({
      group: group.group,
      items: group.items.map((item) => item.name),
    }));
  }, []);

  /* ---------------- Registration ID Logic ---------------- */

  const getRegistrationIdType = (headquartersCountry: string, selectedType?: string) => {
    if (headquartersCountry === "India" && selectedType) {
      return selectedType; // GST, CIN, UDM
    }
    if (!headquartersCountry) return "Registration Number";
    for (const group of countriesData.countryGroups) {
      const countryItem = group.items.find((item) => item.name === headquartersCountry);
      if (countryItem) {
        return countryItem.registrationName;
      }
    }
    return "Registration Number";
  };

  const getRegistrationPlaceholder = (headquartersCountry: string, selectedType?: string) => {
    if (headquartersCountry === "India") {
      return selectedType ? `Enter ${selectedType}` : "Select Registration Type first";
    }
    return `Enter ${getRegistrationIdType(headquartersCountry)}`;
  };

  /* ---------------- Address Logic ---------------- */

  const handleRegisteredAddressChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      registeredAddress: value,
      currentAddress: sameAsRegistered ? value : prev.currentAddress,
    }));
  };

  const handleSameAsRegisteredChange = (checked: boolean) => {
    setSameAsRegistered(checked);
    setFormData((prev) => ({
      ...prev,
      currentAddress: checked ? prev.registeredAddress : "",
    }));
  };

  /* ---------------- Submit ---------------- */

  const handleSignup = async () => {
    if (!isFormValid) return;

    setLoading(true);
    try {
      const result = await trpc.auth.registerWithCompany.mutate({
        email: formData.email,
        password: formData.password,
        userName: formData.name,
        userDesignation: formData.designation,
        userPhoneNumber: `${formData.phoneCountryCode}${formData.phoneNumber}`,
        companyName: formData.companyName,
        headquartersCountry: formData.country,
        headquartersCity: formData.city,
        registrationNumber: formData.registrationNumber,
        registrationIdType: (formData.registrationIdType || undefined) as any,
        companyRegisteredAddress: formData.registeredAddress,
        currentAddress: formData.currentAddress,
      });

      // Temporarily set token to authenticate document uploads
      saveToken(result.token);

      if (mandatoryData.file) {
        const fd = new FormData();
        fd.append("file", mandatoryData.file);
        fd.append("category", "AFFIDAVIT");
        fd.append("companyId", result.companyId);
        fd.append("name", "Affidavit");
        await trpc.file.upload.mutate(fd as any);
      }

      if (authorityLetterData.file) {
        const fd = new FormData();
        fd.append("file", authorityLetterData.file);
        fd.append("category", "AUTHORITY_LETTER");
        fd.append("companyId", result.companyId);
        fd.append("name", "Authority Letter");
        await trpc.file.upload.mutate(fd as any);
      }

      // Clear temp token — user must verify email then log in properly
      removeToken();

      successNotification({
        title: "Registration Successful!",
        message: "Please check your email to verify your account.",
      });

      navigate(`/resend-email?email=${encodeURIComponent(formData.email)}`);
      setEmailModalOpened(true);
    } catch (err) {
      removeToken();
      handleTrpcError(err);
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- Modal Continue ---------------- */

  const handleModalContinue = () => {
    setEmailModalOpened(false);
    navigate("/registration-success", {
      state: {
        companyName: formData.companyName,
        location: `${formData.city}, ${formData.country}`,
        email: formData.email,
      },
    });
  };

  /* ---------------- UI ---------------- */

  return (
    <Box mih="100vh" py={40}>
      <Container size="lg">
        <Flex direction="column" align="center" mb="xl">
          <img src="/gtLogo.svg" alt="GT Setu" style={{ height: 84, width: "auto" }} />
        </Flex>

        <Paper shadow="sm" p="xl" radius="lg" maw={900} mx="auto" style={{ border: "1px solid #E5E7EB" }}>
          <Stepper active={currentStep} mb="xl">
            <Stepper.Step label="Company Details" />
            <Stepper.Step label="Documents" />
          </Stepper>

          {/* ── Step 0: Company Details ── */}
          {currentStep === 0 && (
            <>
              {/* Section 1: Personal Details */}
              <Title order={4} mb="md" c="slate.7" style={{ borderBottom: "1px solid #eee", paddingBottom: "8px" }}>
                Personal Details
              </Title>

              <SimpleGrid cols={{ base: 1, sm: 2 }} mb="md">
                <TextInput
                  label="Contact Person Name"
                  placeholder="Enter name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.currentTarget.value })}
                />
                <TextInput
                  label="Designation"
                  placeholder="Enter designation"
                  required
                  value={formData.designation}
                  onChange={(e) => setFormData({ ...formData, designation: e.currentTarget.value })}
                />
              </SimpleGrid>

              <SimpleGrid cols={{ base: 1, sm: 2 }} mb="xl">
                <Box>
                  <Text fz="sm" fw={500} mb={3}>
                    Phone Number{" "}
                    <Text span c="red">
                      *
                    </Text>
                  </Text>
                  <PhoneInput
                    defaultCountry="in"
                    value={`${formData.phoneCountryCode}${formData.phoneNumber}`}
                    onChange={(phone, meta) => {
                      const countryCode = meta.country?.dialCode ? `+${meta.country.dialCode}` : "";
                      const nationalNumber = phone.replace(`+${meta.country?.dialCode || ""}`, "").trim();
                      setFormData((prev) => ({ ...prev, phoneCountryCode: countryCode, phoneNumber: nationalNumber }));
                    }}
                    style={{
                      width: "100%",
                    }}
                    inputStyle={{
                      width: "100%",
                      height: "36px",
                      fontSize: "14px",
                      border: "1px solid var(--mantine-color-gray-3)",
                      borderLeft: "none",
                      borderRadius: "0 var(--mantine-radius-md) var(--mantine-radius-md) 0",
                      outline: "none",
                    }}
                    countrySelectorStyleProps={{
                      buttonStyle: {
                        height: "36px",
                        border: "1px solid var(--mantine-color-gray-3)",
                        borderRadius: "var(--mantine-radius-md) 0 0 var(--mantine-radius-md)",
                        padding: "0 12px",
                        backgroundColor: "white",
                      },
                      dropdownStyleProps: {
                        style: {
                          backgroundColor: "white",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                          border: "1px solid var(--mantine-color-gray-3)",
                          zIndex: 1000,
                        },
                      },
                    }}
                  />
                </Box>
                <Box />
              </SimpleGrid>

              {/* Section 2: Company Details */}
              <Title order={4} mb="md" c="slate.7" style={{ borderBottom: "1px solid #eee", paddingBottom: "8px" }}>
                Company Details
              </Title>

              <SimpleGrid cols={{ base: 1, sm: 2 }} mb="md">
                <TextInput
                  label="Company Name"
                  placeholder="Enter your company name"
                  required
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.currentTarget.value })}
                  leftSection={<IconBuilding size={18} />}
                />
                <TextInput
                  label="Company Email Address"
                  required
                  type="email"
                  placeholder="Enter email Id"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.currentTarget.value })}
                  leftSection={<IconMail size={18} />}
                  error={
                    formData.email && !isValidEmail(formData.email)
                      ? "Please enter a valid email address"
                      : formData.email && isValidEmail(formData.email) && !isBusinessEmail(formData.email)
                      ? "Personal email addresses are not allowed. Please use your official business email."
                      : null
                  }
                />
              </SimpleGrid>

              <SimpleGrid cols={{ base: 1, sm: 2 }} mb="md">
                <Select
                  label="Headquarters Country"
                  placeholder="Enter country"
                  required
                  searchable
                  value={formData.country}
                  onChange={(value) => setFormData({ ...formData, country: value || "", registrationIdType: null })}
                  data={countryOptions}
                  leftSection={<IconWorld size={18} />}
                />

                <TextInput
                  label="Headquarters City"
                  placeholder="Enter city"
                  required
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.currentTarget.value })}
                  leftSection={<IconMapPin size={18} />}
                />
              </SimpleGrid>

              <SimpleGrid cols={{ base: 1, sm: 2 }} mb="md">
                {isIndia ? (
                  <Flex gap="sm" align="flex-end">
                    <Select
                      label="Registration ID Type"
                      placeholder="Type"
                      required
                      w={160}
                      style={{ flexShrink: 0 }}
                      data={[
                        { value: "GST", label: "GST" },
                        { value: "CIN", label: "CIN" },
                        { value: "UDM", label: "UDM (Udyam)" },
                      ]}
                      value={formData.registrationIdType}
                      onChange={(value) => setFormData({ ...formData, registrationIdType: value || null })}
                    />
                    <TextInput
                      label={getRegistrationIdType(formData.country, formData.registrationIdType ?? undefined)}
                      placeholder={getRegistrationPlaceholder(
                        formData.country,
                        formData.registrationIdType ?? undefined
                      )}
                      required
                      style={{ flex: 1 }}
                      styles={{ label: { whiteSpace: "nowrap" } }}
                      value={formData.registrationNumber}
                      onChange={(e) => setFormData({ ...formData, registrationNumber: e.currentTarget.value })}
                      leftSection={<IconId size={18} />}
                      disabled={isIndia && !formData.registrationIdType}
                    />
                  </Flex>
                ) : (
                  <TextInput
                    label={getRegistrationIdType(formData.country, formData.registrationIdType ?? undefined)}
                    placeholder={getRegistrationPlaceholder(formData.country, formData.registrationIdType ?? undefined)}
                    required
                    value={formData.registrationNumber}
                    onChange={(e) => setFormData({ ...formData, registrationNumber: e.currentTarget.value })}
                    leftSection={<IconId size={18} />}
                  />
                )}
                <Box />
              </SimpleGrid>

              <SimpleGrid cols={{ base: 1, sm: 2 }} mb="xl">
                <PasswordInput
                  label="Password"
                  placeholder="Enter password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.currentTarget.value })}
                  visible={showPassword}
                  onVisibilityChange={setShowPassword}
                  leftSection={<IconLock size={18} />}
                  error={
                    formData.password.length > 0 && !isPasswordValid ? "Password must be at least 8 characters" : null
                  }
                />

                <PasswordInput
                  label="Confirm Password"
                  placeholder="Re-enter password"
                  required
                  value={formData.confirmPassword}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      confirmPassword: e.currentTarget.value,
                    })
                  }
                  visible={showConfirmPassword}
                  onVisibilityChange={setShowConfirmPassword}
                  leftSection={<IconLock size={18} />}
                  error={formData.confirmPassword && !doPasswordsMatch ? "Passwords don't match" : null}
                />
              </SimpleGrid>

              <Textarea
                label="Company Registered Address"
                required
                placeholder="Enter company registered address"
                autosize
                minRows={3}
                value={formData.registeredAddress}
                onChange={(e) => handleRegisteredAddressChange(e.currentTarget.value)}
                mb="md"
              />

              <Checkbox
                label="Company current address is same as registered address"
                checked={sameAsRegistered}
                onChange={(e) => handleSameAsRegisteredChange(e.currentTarget.checked)}
                mb="sm"
              />

              <Textarea
                label="Company Current Address"
                placeholder="Enter company current address"
                autosize
                minRows={3}
                value={formData.currentAddress}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    currentAddress: e.currentTarget.value,
                  })
                }
                disabled={sameAsRegistered}
                required={!sameAsRegistered}
                mb="md"
              />

              <Flex justify="flex-end">
                <Button size="md" px="xl" onClick={() => setCurrentStep(1)} disabled={!isFormValid}>
                  Next
                </Button>
              </Flex>
            </>
          )}

          {/* ── Step 1: Documents ── */}
          {currentStep === 1 && (
            <>
              <Title order={4} mb={4} fw={400}>
                Documents
              </Title>
              <Text fz="sm" c="slate.6" mb="lg">
                Upload at least one of the following documents for verification: Authority Letter or Affidavit.
              </Text>

              {/* Authority Letter */}
              <Box mb="sm">
                <Flex justify="space-between" align="flex-start" mb="xs">
                  <Box>
                    <Text fz="sm" fw={500}>
                      Authority Letter
                    </Text>
                    <Text fz="xs" c="slate.6">
                      Download the template, sign it, and upload the signed document.
                    </Text>
                  </Box>
                  <Badge color="red" variant="light" size="sm" radius="xl">
                    MANDATORY
                  </Badge>
                </Flex>
                {/* Template box — always visible */}
                <Paper withBorder shadow="none" p="sm" radius="md" mb="xs" style={{ borderColor: "#E5E7EB" }}>
                  <Flex align="center" gap="sm">
                    <Box bg="gray.1" p="xs" style={{ borderRadius: "50%", display: "inline-flex", flexShrink: 0 }}>
                      <IconDownload size={16} color="var(--mantine-color-slate-6)" />
                    </Box>
                    <Box flex={1}>
                      <Text fw={500} fz="sm">
                        Download Authority Letter Template
                      </Text>
                      <Text fz="xs" c="slate.6">
                        Download, sign, and upload the signed authority letter
                      </Text>
                    </Box>
                    {authorityLetterTemplateUrl ? (
                      <Button
                        component="a"
                        href={authorityLetterTemplateUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="xs"
                        variant="filled"
                        color="primary"
                        leftSection={<IconDownload size={13} />}
                      >
                        Download
                      </Button>
                    ) : (
                      <Button
                        size="xs"
                        variant="filled"
                        color="primary"
                        leftSection={<IconDownload size={13} />}
                        disabled
                      >
                        Download
                      </Button>
                    )}
                  </Flex>
                </Paper>
                {authorityLetterData.file ? (
                  <Flex
                    align="center"
                    justify="space-between"
                    p="sm"
                    style={{ border: "1px solid #E5E7EB", borderRadius: 8 }}
                  >
                    <Flex align="center" gap="xs">
                      <IconFileText size={18} color="#2563EB" />
                      <Text fz="sm">{authorityLetterData.file.name}</Text>
                    </Flex>
                    <Button
                      variant="subtle"
                      color="red"
                      size="xs"
                      onClick={() => handleUpdateAuthorityLetter("file", null)}
                    >
                      Remove
                    </Button>
                  </Flex>
                ) : hasAffidavit ? (
                  <Text fz="xs" c="slate.4">
                    You've uploaded an Affidavit. Remove it to upload an Authority Letter instead.
                  </Text>
                ) : (
                  <Paper withBorder shadow="none" p="md" radius="md" style={{ borderColor: "#E5E7EB" }}>
                    <Flex justify="space-between" align="center">
                      <Flex align="center" gap="sm">
                        <IconFileText size={28} color="var(--mantine-color-slate-4)" />
                        <Box>
                          <Text fw={500} fz="sm">
                            No document uploaded
                          </Text>
                          <Text fz="xs" c="slate.6">
                            Upload a signed authority letter
                          </Text>
                        </Box>
                      </Flex>
                      <FileButton
                        onChange={(file) => {
                          if (file && file.size > 10 * 1024 * 1024) {
                            errorNotification({ title: "File too large", message: "Max 10MB allowed." });
                            return;
                          }
                          handleUpdateAuthorityLetter("file", file);
                        }}
                        accept=".pdf,.doc,.docx"
                      >
                        {(props) => (
                          <Button {...props} size="xs" variant="outline" leftSection={<IconUpload size={13} />}>
                            Upload
                          </Button>
                        )}
                      </FileButton>
                    </Flex>
                  </Paper>
                )}
              </Box>

              <Flex align="center" my="md">
                <Box style={{ flex: 1, height: 1, background: "#E5E7EB" }} />
                <Text fz="xs" px="sm" c="slate.5">
                  OR
                </Text>
                <Box style={{ flex: 1, height: 1, background: "#E5E7EB" }} />
              </Flex>

              {/* Affidavit */}
              <Box mb="sm">
                <Flex justify="space-between" align="flex-start" mb="xs">
                  <Box>
                    <Text fz="sm" fw={500}>
                      Affidavit
                    </Text>
                    <Text fz="xs" c="slate.6">
                      Download the template, sign it, and upload the signed document.
                    </Text>
                  </Box>
                  <Badge color="red" variant="light" size="sm" radius="xl">
                    MANDATORY
                  </Badge>
                </Flex>
                {/* Template box — always visible */}
                <Paper withBorder shadow="none" p="sm" radius="md" mb="xs" style={{ borderColor: "#E5E7EB" }}>
                  <Flex align="center" gap="sm">
                    <Box bg="gray.1" p="xs" style={{ borderRadius: "50%", display: "inline-flex", flexShrink: 0 }}>
                      <IconDownload size={16} color="var(--mantine-color-slate-6)" />
                    </Box>
                    <Box flex={1}>
                      <Text fw={500} fz="sm">
                        Download Affidavit Template
                      </Text>
                      <Text fz="xs" c="slate.6">
                        Download, sign, and upload the signed affidavit
                      </Text>
                    </Box>
                    {affidavitTemplateUrl ? (
                      <Button
                        component="a"
                        href={affidavitTemplateUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="xs"
                        variant="filled"
                        color="primary"
                        leftSection={<IconDownload size={13} />}
                      >
                        Download
                      </Button>
                    ) : (
                      <Button
                        size="xs"
                        variant="filled"
                        color="primary"
                        leftSection={<IconDownload size={13} />}
                        disabled
                      >
                        Download
                      </Button>
                    )}
                  </Flex>
                </Paper>
                {mandatoryData.file ? (
                  <Flex
                    align="center"
                    justify="space-between"
                    p="sm"
                    style={{ border: "1px solid #E5E7EB", borderRadius: 8 }}
                  >
                    <Flex align="center" gap="xs">
                      <IconFileText size={18} color="#2563EB" />
                      <Text fz="sm">{mandatoryData.file.name}</Text>
                    </Flex>
                    <Button variant="subtle" color="red" size="xs" onClick={() => handleUpdateMandatory("file", null)}>
                      Remove
                    </Button>
                  </Flex>
                ) : hasAuthorityLetter ? (
                  <Text fz="xs" c="slate.4">
                    You've uploaded an Authority Letter. Remove it to upload an Affidavit instead.
                  </Text>
                ) : (
                  <Paper withBorder shadow="none" p="md" radius="md" style={{ borderColor: "#E5E7EB" }}>
                    <Flex justify="space-between" align="center">
                      <Flex align="center" gap="sm">
                        <IconFileText size={28} color="var(--mantine-color-slate-4)" />
                        <Box>
                          <Text fw={500} fz="sm">
                            No document uploaded
                          </Text>
                          <Text fz="xs" c="slate.6">
                            Upload a signed affidavit
                          </Text>
                        </Box>
                      </Flex>
                      <FileButton
                        onChange={(file) => {
                          if (file && file.size > 10 * 1024 * 1024) {
                            errorNotification({ title: "File too large", message: "Max 10MB allowed." });
                            return;
                          }
                          handleUpdateMandatory("file", file);
                        }}
                        accept=".pdf,.doc,.docx"
                      >
                        {(props) => (
                          <Button {...props} size="xs" variant="outline" leftSection={<IconUpload size={13} />}>
                            Upload
                          </Button>
                        )}
                      </FileButton>
                    </Flex>
                  </Paper>
                )}
              </Box>

              <Flex justify="space-between" mt="xl">
                <Button variant="subtle" onClick={() => setCurrentStep(0)}>
                  Back
                </Button>
                <Button
                  size="md"
                  px="xl"
                  onClick={handleSignup}
                  loading={loading}
                  disabled={!hasRequiredDocument || loading}
                >
                  Register
                </Button>
              </Flex>
            </>
          )}
        </Paper>

        <Flex justify="center" mt="md">
          <Anchor component={Link} to="/login" size="sm" c="slate.6">
            ← Back to Login
          </Anchor>
        </Flex>

        <VerifyEmailModal
          opened={emailModalOpened}
          onClose={() => setEmailModalOpened(false)}
          email={formData.email}
          onContinue={handleModalContinue}
        />
      </Container>
    </Box>
  );
}

export default SignupPage;
